import { Component, ElementRef, Inject, Injector, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, delay, Observable } from 'rxjs';
import { AbstractComponent } from '../../../commons/abstract.component';
import { DEFAULT_LOCALE, LOCALE_LABELS, SUPPORTED_LOCALES, SupportedLocale } from '../../../i18n/supported-locale';
import { LanguageService } from '../../../i18n/language.service';
import { Company } from '../../../models/company.model';
import { ConfirmationComponent } from '../confirmation/confirmation.component';
import { CompanyService } from './../../../services/company.service';
import { ContactService } from './../../../services/contact.service';
import { FirebaseService } from './../../../services/storage/firebase/firebase.service';
import { UploadTypeEnum } from './../../../services/storage/upload.type.enum';

export type EditBasicInfoCompanyDialogData = {
  company?: Company;
  companyId?: string;
};

@Component({
    selector: 'app-edit-basic-info-company',
    templateUrl: './edit-basic-info-company.component.html',
    styleUrls: ['./edit-basic-info-company.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditBasicInfoCompanyComponent extends AbstractComponent {
  private loadingSpinnerSubject = new BehaviorSubject<boolean>(false);
  loadingSpinner$: Observable<boolean> = this.loadingSpinnerSubject
    .asObservable()
    .pipe(delay(700));

  @ViewChild('fileInputFile', { static: false }) inputFile: ElementRef;

  company: Company;
  file: File;

  readonly locales: ReadonlyArray<SupportedLocale> = SUPPORTED_LOCALES;
  readonly localeLabels: Record<SupportedLocale, string> = LOCALE_LABELS;

  constructor(
    injector: Injector,
    private firebaseService: FirebaseService,
    private companyService: CompanyService,
    private languageService: LanguageService,
    override formBuilder: FormBuilder,
    public contactService: ContactService,
    public dialog: MatDialog,
    private dialogRef: MatDialogRef<EditBasicInfoCompanyComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditBasicInfoCompanyDialogData,
  ) {
    super(injector);
    this.createForm();
    this.company = (data?.company ?? ({} as Company));
  }

  ngOnInit(): void {
    const idCompany = super.getIDCurrentCompany();
    if (idCompany) {
      this.loading = true;
      this.companyService.getById(idCompany).subscribe({
        next: (company) => {
          this.company = company;
          this.formGroup.patchValue(this.company);
        },
        error: (err) => {
          this.alertService.error(err.error?.message || this.t('shared.editBasicInfoCompany.loadError'));
          this.dialogRef.close();
        },
        complete: () => {
          this.loading = false;
        },
      });
      return;
    }

    this.formGroup.patchValue(this.company);
  }

  private createForm(): void {
    this.formGroup = this.formBuilder.group({
      tin: [null, [Validators.required]],
      name: [
        null,
        [Validators.required, Validators.minLength(3), Validators.maxLength(255)],
      ],
      email: [null, [Validators.required, Validators.email]],
      phone: [null, [Validators.required]],
      description: [null, []],
      image: [null, []],
      address: [null, []],
      city: [null, []],
      state: [null, []],
      zip: [null, []],
    });

    this.formGroup.valueChanges.subscribe((value) => {
      Object.assign(this.company, value);
    });

    this.formGroup.get('zip')?.valueChanges.subscribe((value) => {
      if (value && value.length === 8) {
        this.onCep(value);
      }
    });
  }

  get companyImage() {
    return (
      this.formGroup &&
      this.formGroup.get('image') &&
      this.formGroup.get('image').value
    );
  }

  close(): void {
    this.dialogRef.close();
  }

  get currentCompanyLanguage(): SupportedLocale {
    return (this.company?.language as SupportedLocale) ?? DEFAULT_LOCALE;
  }

  // Company default operating language (RF-01, RF-12). Admin-only — persisted
  // via PUT /api/company/:id/language (T-15), which enforces the ADMIN role.
  onCompanyLanguageChange(language: SupportedLocale): void {
    if (!this.company?._id) {
      return;
    }
    this.companyService.setLanguage(this.company._id, language).subscribe({
      next: () => { this.company.language = language; },
      error: (err) => this.alertService.error(err.error?.message || this.t('shared.editBasicInfoCompany.languageUpdateError')),
    });
  }

  save(): void {
    if (!this.formGroup.valid) {
      this.validateAllFormFields(this.formGroup);
      return;
    }

    this.loading = true;

    this.companyService.update(this.company._id, this.company).subscribe({
      next: (updated) => {
        this.alertService.success();

        // Update the language to reflect the change immediately for the current USER.
        this.languageService.setLanguage(this.company.language, true);
        this.dialogRef.close(updated);
      },
      error: (err) => {
        this.alertService.error(err.error?.message || this.t('shared.editBasicInfoCompany.updateError'));
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  addImage(): void {
    this.inputFile?.nativeElement?.click();
  }

  private isValidImage(file: File): boolean {
    const maxFileSize = 1 * 1024 * 1024;
    const validTypes = ['image/jpeg', 'image/png'];

    if (file.size > maxFileSize) {
      this.alertService.warning(this.t('shared.editBasicInfoCompany.imageMaxSizeWarning'));
      return false;
    }

    if (!validTypes.includes(file.type)) {
      this.alertService.warning(this.t('shared.editBasicInfoCompany.invalidFormatWarning'));
      return false;
    }

    return true;
  }

  private validateImageDimensions(file: File): Promise<boolean> {
    return new Promise((resolve) => {
      if (!(file instanceof File)) {
        resolve(false);
        return;
      }

      const img = new Image();
      img.onload = () => {
        const isValid = img.width <= 800 && img.height <= 400;
        resolve(isValid);
      };
      img.onerror = () => resolve(false);

      const objectURL = URL.createObjectURL(file);
      img.src = objectURL;
    });
  }

  async onSelectImage(event: Event): Promise<void> {
    this.loadingSpinnerSubject.next(true);

    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];

    if (!file) {
      this.loadingSpinnerSubject.next(false);
      return;
    }

    const isValidSize = await this.validateImageDimensions(file);
    if (!isValidSize) {
      this.alertService.warning(this.t('shared.editBasicInfoCompany.imageMaxDimensionsWarning'));
      this.loadingSpinnerSubject.next(false);
      return;
    }

    if (!this.isValidImage(file)) {
      this.loadingSpinnerSubject.next(false);
      return;
    }

    this.file = file;

    const filename = new Date().getTime().toString();
    try {
      const snapshot = await this.firebaseService.uploadFile(
        this.file,
        filename,
        UploadTypeEnum.COMPANY,
      );
      this.formGroup.patchValue({ image: snapshot.url });
    } catch (error) {
      this.alertService.error(this.t('shared.editBasicInfoCompany.imageUploadError'));
    } finally {
      this.loadingSpinnerSubject.next(false);
      input.value = '';
    }
  }

  removeMedia(): void {
    if (!this.company?.image) {
      this.formGroup.patchValue({ image: null });
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '600px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (Boolean(result) === true) {
        this.loadingSpinnerSubject.next(true);
        this.firebaseService
          .deleteFile(this.company.image)
          .then(() => {
            this.formGroup.patchValue({ image: null });
            this.company.image = null;

            if (this.inputFile?.nativeElement) {
              this.inputFile.nativeElement.value = '';
            }
          })
          .catch(() => {
            this.alertService.error(this.t('shared.editBasicInfoCompany.fileDeleteError'));
          })
          .finally(() => {
            this.loadingSpinnerSubject.next(false);
          });
      }
    });
  }

  onImageLoadError(): void {
    this.formGroup.patchValue({ image: null });
    this.company.image = null;
  }

  onCep(cep: string): void {
    if (cep?.length === 8) {
      this.getCep(cep);
    }
  }

  private getCep(cep: string): void {
    this.contactService.searchCep(cep).subscribe({
      next: (res) => {
        this.formGroup.patchValue({
          address: res.street,
          city: res.city,
          state: res.state,
        });
      },
      error: () => this.alertService.error(this.t('shared.editBasicInfoCompany.cepFetchError')),
    });
  }

  keyPressOnlyNumbers(event: any) {
    const pattern = /[0-9\+\-\ ]/;
    const inputChar = String.fromCharCode(event.charCode);
    if (event.keyCode !== 8 && !pattern.test(inputChar)) {
      event.preventDefault();
    }
  }
}
